import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Scan, CheckCircle2, Trash2, FileText, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import StaffIdInput from '@/components/StaffIdInput';

export default function WIPDamageClearance() {
  const navigate = useNavigate();
  const scanInputRef = useRef(null);

  const [staffId, setStaffId] = useState('');
  const [staffName, setStaffName] = useState('');
  const [staffLocked, setStaffLocked] = useState(false);

  const [scanValue, setScanValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [sessionLog, setSessionLog] = useState([]);
  const [summary, setSummary] = useState(null);

  const focusScan = useCallback(() => {
    setTimeout(() => scanInputRef.current?.focus(), 80);
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await apiClient.get('/damage/summary');
      setSummary(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { if (staffLocked) focusScan(); }, [staffLocked, focusScan]);

  const handleStaffValidated = (result) => {
    if (result.valid) {
      setStaffId(result.staffId);
      setStaffName(result.staffName || '');
      setStaffLocked(true);
      focusScan();
    }
  };

  // Auto-accept: scanning a USN validates then immediately marks it damaged.
  const handleScan = async (e) => {
    e.preventDefault();
    const usn = scanValue.trim();
    if (!usn || isProcessing) return;

    setIsProcessing(true);
    try {
      // 1) validate the scanned USN
      const validated = await apiClient.post(`/damage/scan-usn?usn_code=${encodeURIComponent(usn)}`);
      const item = validated.data;

      // 2) immediately confirm the damage (no remarks, no confirm step)
      const res = await apiClient.post('/damage/confirm-damage', {
        usn_code: item.usn_code,
        bundle_code: item.bundle_code,
        remarks: '',
        staff_id: staffId,
      });

      setSessionLog(prev => [{
        usn_code: item.usn_code,
        bundle_code: item.bundle_code,
        design_name: item.design_name,
        bundle_completed: res.data.bundle_completed,
        time: new Date().toLocaleTimeString(),
      }, ...prev]);

      if (res.data.bundle_completed) {
        toast.success(`USN ${item.usn_code} damaged. Bundle ${item.bundle_code} fully accounted — moved to PROCESSED`);
      } else {
        toast.success(`USN ${item.usn_code} marked as damaged`);
      }

      setScanValue('');
      fetchSummary();
      focusScan();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark USN as damaged');
      setScanValue('');
      focusScan();
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/wms/sfg')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-button">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-base font-semibold">WIP Damage Clearance</h1>
            <p className="text-xs text-gray-400">Scan damaged pieces to clear from WIP</p>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-4 space-y-4">
        {/* Summary cards */}
        {summary && (
          <div className="grid grid-cols-3 gap-3" data-testid="damage-summary">
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-red-700">{summary.total}</p>
                <p className="text-xs text-red-600">Total Damaged</p>
              </CardContent>
            </Card>
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-amber-700">{summary.damaged_pending_receipt}</p>
                <p className="text-xs text-amber-600">Pending Receipt</p>
              </CardContent>
            </Card>
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-emerald-700">{summary.received}</p>
                <p className="text-xs text-emerald-600">Received</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Staff identification */}
        <Card data-testid="staff-section">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <User className="h-5 w-5 text-[#2D5F4F]" />
              <span className="font-medium text-sm">Staff Identification</span>
            </div>
            {!staffLocked ? (
              <StaffIdInput onValidated={handleStaffValidated} autoFocus label="" data-testid="damage-staff-input" />
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-[#2D5F4F] text-white px-4 py-2 rounded-lg font-mono text-sm" data-testid="damage-staff-locked">{staffId}</div>
                  {staffName && <span className="text-sm text-[#5A5F6E]">{staffName}</span>}
                </div>
                <Button variant="outline" size="sm" onClick={() => { setStaffLocked(false); setStaffId(''); setStaffName(''); }} data-testid="damage-staff-change">Change</Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Scan — auto-accepts on scan */}
        {staffLocked && (
          <Card data-testid="scan-section">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Scan className="h-5 w-5 text-red-600" />
                <span className="font-medium text-sm">Scan Damaged USN</span>
                <span className="text-xs text-gray-400 ml-auto">Damage is accepted instantly on scan</span>
              </div>
              <form onSubmit={handleScan}>
                <Input
                  ref={scanInputRef}
                  value={scanValue}
                  onChange={e => setScanValue(e.target.value)}
                  placeholder="Scan or enter USN barcode..."
                  disabled={isProcessing}
                  autoFocus
                  data-testid="usn-scan-input"
                />
              </form>
              {isProcessing && <p className="text-xs text-gray-500 mt-2">Processing...</p>}
            </CardContent>
          </Card>
        )}

        {/* Session log */}
        {sessionLog.length > 0 && (
          <Card data-testid="session-log">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Session Log ({sessionLog.length})</span>
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {sessionLog.map((entry, i) => (
                  <div key={i} className="flex items-start gap-2 p-2 bg-gray-50 rounded text-xs" data-testid={`session-log-row-${i}`}>
                    <Trash2 className="h-3.5 w-3.5 text-red-500 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-medium">{entry.usn_code}</span>
                        <span className="text-gray-400">{entry.time}</span>
                      </div>
                      <p className="text-gray-500">{entry.bundle_code} &middot; {entry.design_name || 'N/A'}</p>
                      {entry.bundle_completed && (
                        <span className="inline-flex items-center gap-1 text-emerald-600 font-medium mt-0.5">
                          <CheckCircle2 className="h-3 w-3" /> Bundle fully cleared
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Link to inventory report */}
        <Button
          variant="outline"
          className="w-full"
          onClick={() => navigate('/wms/damage/inventory')}
          data-testid="view-inventory-btn"
        >
          View Damage Inventory Report
        </Button>
      </div>
    </div>
  );
}
